import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { GET_SONG_RINGTONES_URL, REMOVE_SONG_RINGTONE_URL, ADD_SONG_RINGTONE_URL } from '../../../api/song';
import EmptyState from '../../../components/shared/EmptyState';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import { toast } from '../../../components/shared/toast';
import en from '../../../translations/en.json';

/** Resolve a dot-separated key from the translation object */
function t(key) {
  return key.split('.').reduce((obj, k) => (obj ? obj[k] : null), en) || key;
}

/** Convert operator/provider name to Title Case for dropdown */
function toTitleCase(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Returns a consistent accent color derived from the provider name.
 * Same provider → same color across renders.
 */
function providerColor(name = '') {
  const palette = [
    '#f43f5e', // rose
    '#22c55e', // green
    '#ef4444', // red
    '#3b82f6', // blue
    '#a855f7', // purple
    '#f97316', // orange
    '#06b6d4', // cyan
    '#eab308', // yellow
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
  }
  return palette[hash % palette.length];
}

/** Generic provider icon — consistent with the existing card SVG style */
function ProviderIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
      <path d="M9 6h6" />
      <path d="M9 10h6" />
    </svg>
  );
}

function RingtoneTab({ song }) {
  const [ringtones, setRingtones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  // Confirmation modal state for removing
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null); // { ringtoneId, providerName }

  // Add modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [ringtoneCode, setRingtoneCode] = useState('');
  const [contentCode, setContentCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchRingtones = useCallback(async () => {
    if (!song?.id) return;
    setIsLoading(true);
    try {
      const data = await apiClient(GET_SONG_RINGTONES_URL(song.id));
      if (data && data.ringtones) {
        setRingtones(data.ringtones);
      }
    } catch (err) {
      console.warn('Failed to load song ringtones:', err);
      setRingtones([]);
    } finally {
      setIsLoading(false);
    }
  }, [song?.id]);

  useEffect(() => {
    fetchRingtones();
  }, [song?.id]);

  /** Fetch available providers from operators list and open modal */
  const handleOpenAddModal = async () => {
    setIsAddModalOpen(true);
    try {
      const data = await apiClient('/ringtones?limit=1000&status=1');
      if (data && data.ringtones) {
        setAvailableProviders(data.ringtones);
      }
    } catch (err) {
      console.warn('Failed to load active ringtone providers:', err);
    }
  };

  const handleCancelAdd = () => {
    setIsAddModalOpen(false);
    setSelectedProvider('');
    setRingtoneCode('');
    setContentCode('');
  };

  /** Handles adding a new ringtone assignment */
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProvider) {
      toast.warning('Please select a provider');
      return;
    }
    if (!ringtoneCode.trim()) {
      toast.warning('Ringtone Code is required');
      return;
    }
    if (!contentCode.trim()) {
      toast.warning('Content Code is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient(ADD_SONG_RINGTONE_URL(song.id), {
        method: 'POST',
        body: {
          providerId: selectedProvider,
          ringtoneCode: ringtoneCode.trim(),
          contentCode: contentCode.trim()
        }
      });
      toast.success('Ringtone added successfully!');
      handleCancelAdd();
      await fetchRingtones(); // refresh listing immediately
    } catch (err) {
      console.error('Failed to add ringtone:', err);
      toast.error(err.message || 'Failed to add ringtone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Open the confirmation modal instead of window.confirm */
  const handleRemoveClick = (ringtoneId, providerName) => {
    setPendingRemove({ ringtoneId, providerName });
    setConfirmOpen(true);
  };

  /** Called when the user clicks "Remove" inside the modal */
  const handleConfirmRemove = async () => {
    setConfirmOpen(false);
    if (!pendingRemove) return;

    const { ringtoneId, providerName } = pendingRemove;
    setPendingRemove(null);
    setRemovingId(ringtoneId);

    try {
      await apiClient(REMOVE_SONG_RINGTONE_URL(song.id, ringtoneId), { method: 'PATCH' });
      const successMsg = t('songs.ringtoneTab.removeSuccessMsg').replace('{provider}', providerName);
      toast.success(successMsg);
      await fetchRingtones(); // refresh immediately
    } catch (err) {
      console.error('Failed to remove ringtone:', err);
      toast.error(t('songs.ringtoneTab.removeErrorMsg'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemove(null);
  };

  // Build confirmation modal message with provider name interpolated
  const confirmMessage = pendingRemove
    ? t('songs.ringtoneTab.removeConfirmMessage').replace('{provider}', pendingRemove.providerName)
    : '';

  return (
    <div className="ringtone-tab-container">
      {/* Header */}
      <div className="ringtone-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '24px' }}>
        <div className="ringtone-header-left">
          <div className="ringtone-icon-red">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <line x1="12" y1="7" x2="12" y2="13" />
            </svg>
          </div>
          <div className="ringtone-header-text">
            <h3 className="section-title">{t('songs.ringtoneTab.sectionTitle')}</h3>
            <p className="section-subtitle">{t('songs.ringtoneTab.sectionSubtitle')}</p>
          </div>
        </div>

        <button
          className="action-btn-primary"
          onClick={handleOpenAddModal}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', background: 'var(--primary-color)', color: '#fff', border: 'none', fontWeight: '600', height: '40px' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{t('songs.ringtoneTab.btnAddRingtone')}</span>
        </button>
      </div>

      {/* Grid of cards */}
      <div className="ringtone-cards-grid">
        {isLoading ? (
          // Skeleton cards
          [1, 2].map((i) => (
            <div key={i} className="ringtone-card" style={{ opacity: 0.6 }}>
              <div className="ringtone-card-header">
                <div className="ringtone-provider-info">
                  <div className="provider-logo" style={{ background: '#f1f5f9' }} />
                  <div style={{ width: 80, height: 14, background: '#e2e8f0', borderRadius: 4 }} />
                </div>
              </div>
              <div className="ringtone-details-list">
                {[1, 2, 3, 4, 5].map((j) => (
                  <div key={j} className="ringtone-detail-row">
                    <div style={{ width: '35%', height: 10, background: '#e2e8f0', borderRadius: 3 }} />
                    <div style={{ width: '45%', height: 10, background: '#e2e8f0', borderRadius: 3 }} />
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : ringtones.length === 0 ? (
          // Empty state
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState
              title={t('songs.ringtoneTab.emptyTitle')}
              subtitle={t('songs.ringtoneTab.emptySubtitle')}
              hasSearch={false}
            />
          </div>
        ) : (
          ringtones.map((rt) => {
            const color = providerColor(rt.providerName);
            const isRemoving = removingId === rt.ringtoneId;

            return (
              <div key={rt.songRingtoneId} className="ringtone-card">
                {/* Card header */}
                <div className="ringtone-card-header">
                  <div className="ringtone-provider-info">
                    <div
                      className="provider-logo"
                      style={{
                        color,
                        backgroundColor: rt.companyLogo ? 'transparent' : `${color}18`,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {rt.companyLogo ? (
                        <img
                          src={rt.companyLogo}
                          alt={rt.providerName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ProviderIcon />
                      )}
                    </div>
                    <h4 className="provider-name">{rt.providerName}</h4>
                  </div>
                </div>

                {/* Detail rows — all labels from lang file */}
                <div className="ringtone-details-list">
                  <div className="ringtone-detail-row">
                    <span className="rt-label">{t('songs.ringtoneTab.labelStatus')}</span>
                    <span className="rt-value badge-green">{t('songs.ringtoneTab.statusActive')}</span>
                  </div>
                  <div className="ringtone-detail-row">
                    <span className="rt-label">{t('songs.ringtoneTab.labelRingtoneId')}</span>
                    <span className="rt-value text-bold">{rt.ringtoneCode || '—'}</span>
                  </div>
                  <div className="ringtone-detail-row">
                    <span className="rt-label">{t('songs.ringtoneTab.labelContentCode')}</span>
                    <span className="rt-value text-bold">{rt.contentCode || '—'}</span>
                  </div>
                  <div className="ringtone-detail-row">
                    <span className="rt-label">{t('songs.ringtoneTab.labelAddedOn')}</span>
                    <span className="rt-value">{rt.addedDate || '—'}</span>
                  </div>
                  <div className="ringtone-detail-row">
                    <span className="rt-label">{t('songs.ringtoneTab.labelLastUpdate')}</span>
                    <span className="rt-value">{rt.lastUpdate || '—'}</span>
                  </div>
                </div>

                {/* Footer actions */}
                <div className="ringtone-card-actions">
                  <button
                    className="rt-action-btn remove-btn"
                    disabled={isRemoving}
                    onClick={() => handleRemoveClick(rt.ringtoneId, rt.providerName)}
                  >
                    {isRemoving ? (
                      <span>{t('songs.ringtoneTab.btnRemoving')}</span>
                    ) : (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: '4px' }}
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        {t('songs.ringtoneTab.btnRemove')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Confirmation Modal — shown before removing a ringtone */}
      <ConfirmationModal
        isOpen={confirmOpen}
        type="danger"
        title={t('songs.ringtoneTab.removeConfirmTitle')}
        message={confirmMessage}
        confirmLabel={t('songs.ringtoneTab.removeConfirmBtn')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />

      {/* Add Ringtone Modal popup form */}
      {isAddModalOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '400px', padding: '24px' }} role="dialog" aria-modal="true">
            <button
              className="modal-close-icon-btn"
              onClick={handleCancelAdd}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <h3 className="modal-title-heading" style={{ marginBottom: '20px', fontSize: '1.25rem', fontWeight: '700' }}>
              {t('songs.ringtoneTab.addModalTitle')}
            </h3>

            <form onSubmit={handleAddSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  {t('songs.ringtoneTab.labelProvider')} <span className="required-asterisk">*</span>
                </label>
                <select
                  className="form-select-field"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff' }}
                >
                  <option value="">{t('songs.ringtoneTab.placeholderSelectProvider')}</option>
                  {availableProviders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {toTitleCase(p.name)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  {t('songs.ringtoneTab.labelRingtoneCode')} <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="form-input-field"
                  value={ringtoneCode}
                  onChange={(e) => setRingtoneCode(e.target.value)}
                  placeholder={t('songs.ringtoneTab.placeholderRingtoneCode')}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  {t('songs.ringtoneTab.labelContentCode')} <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="form-input-field"
                  value={contentCode}
                  onChange={(e) => setContentCode(e.target.value)}
                  placeholder={t('songs.ringtoneTab.placeholderContentCode')}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div className="modal-footer-actions" style={{ marginTop: '10px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="modal-btn modal-btn-cancel"
                  onClick={handleCancelAdd}
                  style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                >
                  {t('songs.ringtoneTab.btnCancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="modal-btn modal-btn-confirm success"
                  disabled={isSubmitting}
                  style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: 'var(--primary-color)', color: '#fff', fontWeight: '600' }}
                >
                  {isSubmitting ? t('songs.ringtoneTab.btnAdding') : t('songs.ringtoneTab.btnAdd')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RingtoneTab;
